import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Grid, IconButton, Radio, Typography } from '@mui/material';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { CardContent, CardHeader, LeftColumn, StyledCard, StyledTypography } from './GraphTemplateCard.Styled';
import { COLOR_PRIMARY } from '../../../../../attrs/colors';

interface GraphTemplateCardProps {
  graph: {
    id: string | number;
    name: string;
    description: string;
    favorite: boolean;
    period_type: string;
    tmpl_access_type: string;
  };
  isRadio?: boolean;
  checked?: boolean;
  onSelectCard?: React.ChangeEventHandler<HTMLInputElement>;
  onFavoriteChange?: (graphId: string | number) => void;
  notClick?: boolean;
}

export const GraphTemplateCard = ({
  graph,
  isRadio = false,
  checked = false,
  onSelectCard,
  onFavoriteChange,
  notClick = false
}: GraphTemplateCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const navigate = () => {
    if (!isRadio) {
      history.push(`/dashboard/${id}/graph-templates/${graph.id}`);
    }
  };

  const toggleFavorite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onFavoriteChange?.(graph.id);
  };

  const handleCardClick = () => {
    if (!notClick) {
      navigate();
    }
  };

  return (
    <StyledCard
      sx={{ cursor: isRadio ? 'default' : 'pointer' }}
      data-testid="chart/graph-template-single-card"
      key={graph.id}
    >
      <CardContent onClick={handleCardClick} data-testid={`graph-template-open-${graph.id}`}>
        <LeftColumn>
          <CardHeader>
            <Grid container direction="row" alignItems="center" spacing={2}>
              {isRadio && (
                <Grid item xs="auto">
                  <Box display="flex" justifyContent="right" alignItems="center">
                    <Radio
                      data-testid="template-selection-radiobutton"
                      checked={checked}
                      onChange={onSelectCard}
                      value={graph.id}
                      name="dialog-clone-template-select-graph-template"
                      sx={{
                        marginLeft: { xs: 4, sm: 0.5 },
                        '& .MuiSvgIcon-root': {
                          fontSize: 24
                        },
                        color: COLOR_PRIMARY,
                        '&.Mui-checked': {
                          color: COLOR_PRIMARY
                        }
                      }}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs="auto" order={{ xs: 0, md: 0 }}>
                <StyledTypography variant="h6">{graph.name}</StyledTypography>
              </Grid>

              <Grid item xs="auto" order={{ xs: 3, md: 1 }}>
                <Box display="flex" alignItems="center">
                  <Chip
                    label={t(`machines.detail.tabs.templates.tmpl_access_type.${graph.tmpl_access_type}`)}
                    size="small"
                    sx={{ backgroundColor: '#DEF1EE' }}
                  />
                </Box>
              </Grid>

              <Grid item xs="auto" order={{ xs: 2, md: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="textSecondary">
                    {t(`machines.detail.tabs.templates.period_type.${graph.period_type}`)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs="auto" order={{ xs: 1, md: 3 }}>
                <Box display="flex" alignItems="center">
                  <IconButton
                    onClick={toggleFavorite}
                    size="small"
                    aria-pressed={graph.favorite}
                    data-testid={`graph-template-favorite-${graph.id}`}
                  >
                    {graph.favorite ? <Star /> : <StarBorder />}
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </CardHeader>
          <Typography variant="body2" color="textSecondary" sx={{ pt: 2 }}>
            {graph.description}
          </Typography>
        </LeftColumn>
      </CardContent>
    </StyledCard>
  );
};
